import React, { useEffect, useState } from 'react';
import { ShieldCheck, Loader2, RotateCw, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Input } from '../ui/input';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../ui/input-otp';

const RESEND_COOLDOWN = 30;

const errorText = (error, fallback) => {
  const detail = error.response?.data?.detail;
  return typeof detail === 'string' ? detail : fallback;
};

const AdminCodeStep = ({ challenge, onChallengeChange, onCancel, onSuccess }) => {
  const { verifyAdminCode, resendAdminCode } = useAuth();
  const [code, setCode] = useState('');
  const [trustDevice, setTrustDevice] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(RESEND_COOLDOWN);
  const [error, setError] = useState('');
  const [usePass, setUsePass] = useState(false);
  const [passCode, setPassCode] = useState('');
  const value = usePass ? passCode.trim() : code;
  const ready = usePass ? value.startsWith('tp_') && value.length > 10 : code.length === 6;

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const id = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(id);
  }, [cooldown]);

  const submit = async (e) => {
    e?.preventDefault();
    if (!ready) return;
    setVerifying(true);
    setError('');
    try {
      await verifyAdminCode(challenge.challenge_id, value, trustDevice);
      onSuccess();
    } catch (err) {
      const msg = errorText(err, 'Verification failed. Please try again.');
      setError(msg);
      setCode('');
      setPassCode('');
      if (err.response?.status === 400) {
        toast.error(msg);
        onCancel();
      }
    } finally {
      setVerifying(false);
    }
  };

  const resend = async () => {
    setResending(true);
    setError('');
    try {
      const next = await resendAdminCode(challenge.challenge_id);
      onChallengeChange(next);
      setCode('');
      setCooldown(RESEND_COOLDOWN);
      toast.success(`New code sent to ${next.email_masked}`);
    } catch (err) {
      toast.error(errorText(err, 'Could not resend the code.'));
    } finally {
      setResending(false);
    }
  };

  return (
    <div
      className="bg-slate-900 border border-slate-800 rounded-2xl p-8 shadow-2xl animate-in fade-in slide-in-from-bottom-2 duration-300"
      data-testid="admin-2fa-step"
    >
      <div className="w-12 h-12 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center mb-5">
        <ShieldCheck className="w-6 h-6 text-emerald-400" />
      </div>
      <h2 className="text-2xl font-bold text-white mb-1.5">Check your email</h2>
      <p className="text-slate-400 text-sm mb-6" data-testid="admin-2fa-email-hint">
        Admin sign-in needs a second step. Enter the 6-digit code we sent to{' '}
        <span className="text-slate-200 font-medium">{challenge.email_masked}</span>. It expires in 10 minutes.
      </p>

      <form onSubmit={submit} className="space-y-5">
        {usePass ? (
          <Input
            autoFocus
            value={passCode}
            onChange={(e) => setPassCode(e.target.value)}
            placeholder="tp_…"
            autoComplete="off"
            spellCheck={false}
            className="font-mono bg-slate-800 border-slate-700 text-white h-12"
            data-testid="admin-2fa-testing-pass-input"
          />
        ) : (
        <InputOTP
          maxLength={6}
          value={code}
          onChange={(v) => setCode(v.replace(/\D/g, ''))}
          onComplete={() => {}}
          autoFocus
          containerClassName="justify-center"
          data-testid="admin-2fa-code-input"
        >
          <InputOTPGroup>
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <InputOTPSlot
                key={i}
                index={i}
                className="h-12 w-12 text-lg font-semibold text-white border-slate-700 bg-slate-800"
              />
            ))}
          </InputOTPGroup>
        </InputOTP>
        )}
        <button
          type="button"
          onClick={() => { setUsePass((v) => !v); setError(''); }}
          className="text-xs text-slate-500 hover:text-slate-300 underline underline-offset-2 transition-colors"
          data-testid="admin-2fa-toggle-testing-pass"
        >
          {usePass ? 'Use the emailed code instead' : 'Use a testing pass instead'}
        </button>

        {error && (
          <p className="text-sm text-red-400" data-testid="admin-2fa-error">{error}</p>
        )}

        <label className="flex items-center gap-2.5 text-sm text-slate-300 cursor-pointer select-none">
          <Checkbox
            checked={trustDevice}
            onCheckedChange={(v) => setTrustDevice(Boolean(v))}
            className="border-slate-600 data-[state=checked]:bg-emerald-500 data-[state=checked]:border-emerald-500"
            data-testid="admin-2fa-trust-device"
          />
          Trust this device for 30 days
        </label>

        <Button
          type="submit"
          disabled={!ready || verifying}
          className="w-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold py-5"
          data-testid="admin-2fa-verify-btn"
        >
          {verifying ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Verify & sign in'}
        </Button>
      </form>

      <div className="flex items-center justify-between mt-5 text-sm">
        <button
          type="button"
          onClick={onCancel}
          className="flex items-center gap-1.5 text-slate-500 hover:text-slate-300 transition-colors"
          data-testid="admin-2fa-cancel-btn"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back
        </button>
        <button
          type="button"
          onClick={resend}
          disabled={cooldown > 0 || resending}
          className="flex items-center gap-1.5 text-emerald-400 hover:text-emerald-300 disabled:text-slate-600 transition-colors"
          data-testid="admin-2fa-resend-btn"
        >
          <RotateCw className={`w-3.5 h-3.5 ${resending ? 'animate-spin' : ''}`} />
          {cooldown > 0 ? `Resend code in ${cooldown}s` : 'Resend code'}
        </button>
      </div>
    </div>
  );
};

export default AdminCodeStep;
